import React from "react";
import { Plus, Trash2 } from "lucide-react";

export const TextField = ({ label, value, onChange, placeholder, type = "text", textarea, rows = 4 }) => (
  <label className="block">
    {label && (
      <span className="text-xs font-medium uppercase tracking-wider text-[color:var(--muted)]">
        {label}
      </span>
    )}
    {textarea ? (
      <textarea
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full bg-transparent border border-black/15 rounded-lg focus:border-[color:var(--coral)] outline-none px-3 py-2.5 text-base transition-colors resize-y"
      />
    ) : (
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full bg-transparent border-b border-black/25 focus:border-[color:var(--coral)] outline-none py-2.5 text-base transition-colors"
      />
    )}
  </label>
);

export const Section = ({ title, hint, children }) => (
  <section className="p-6 md:p-8 rounded-[20px] bg-white border border-black/10">
    <div className="flex items-baseline justify-between gap-4">
      <h2 className="font-display text-xl md:text-2xl font-semibold">{title}</h2>
      {hint && <div className="text-xs text-[color:var(--muted)]">{hint}</div>}
    </div>
    <div className="mt-5 space-y-5">{children}</div>
  </section>
);

export const StringList = ({ label, items = [], onChange, placeholder = "Add item" }) => {
  const update = (idx, v) => {
    const next = [...items];
    next[idx] = v;
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, ""]);
  return (
    <div>
      {label && (
        <div className="text-xs font-medium uppercase tracking-wider text-[color:var(--muted)] mb-2">
          {label}
        </div>
      )}
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              value={it || ""}
              onChange={(e) => update(i, e.target.value)}
              placeholder={placeholder}
              className="flex-1 bg-transparent border border-black/15 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--coral)] outline-none"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="w-9 h-9 rounded-full border border-black/15 hover:bg-black/5 grid place-items-center"
              aria-label="Remove"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="mt-2 inline-flex items-center gap-1.5 text-sm text-[color:var(--coral)] hover:underline"
      >
        <Plus className="w-4 h-4" /> Add
      </button>
    </div>
  );
};

export const KeyValueList = ({ label, items = [], onChange, keyName = "value", valueName = "label", keyPlaceholder, valuePlaceholder }) => {
  const update = (idx, field, v) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: v };
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { [keyName]: "", [valueName]: "" }]);
  return (
    <div>
      {label && (
        <div className="text-xs font-medium uppercase tracking-wider text-[color:var(--muted)] mb-2">{label}</div>
      )}
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <input
              value={it?.[keyName] || ""}
              onChange={(e) => update(i, keyName, e.target.value)}
              placeholder={keyPlaceholder || keyName}
              className="sm:w-1/3 bg-transparent border border-black/15 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--coral)] outline-none"
            />
            <input
              value={it?.[valueName] || ""}
              onChange={(e) => update(i, valueName, e.target.value)}
              placeholder={valuePlaceholder || valueName}
              className="flex-1 bg-transparent border border-black/15 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--coral)] outline-none"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="w-9 h-9 rounded-full border border-black/15 hover:bg-black/5 grid place-items-center self-end sm:self-auto"
              aria-label="Remove"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="mt-2 inline-flex items-center gap-1.5 text-sm text-[color:var(--coral)] hover:underline"
      >
        <Plus className="w-4 h-4" /> Add
      </button>
    </div>
  );
};

export const PageHeader = ({ label, title, action }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
    <div>
      <div className="text-sm uppercase tracking-wider text-[color:var(--muted)]">{label}</div>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold">{title}</h1>
    </div>
    {action}
  </div>
);
